import React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import i18n from "../../../i18n/i18n";
import {routingAddresses} from "../../../helpers/routing";
import Button from "../../../common/components/shared/button/button";
import {Api} from "../../../helpers/api";
import {storesContext} from "../../../common/stores/stores";
import {TimetableService} from "../../services/timetableService";
import {NewTimetableActionModel, TimetableActionTypes} from "../../models/timetable";
import {ITimetableStore} from "../../stores/timetableStore";
import {DateUtils} from "../../../helpers/dateUtils";
import {observer} from "mobx-react";
import FormSection from "./formSection/formSection";
import "./actionForm.scss";
import {IActionInStore} from "../../stores/actionInStore";
import {Popup} from "../../../common/components/shared/popup/popup";
import {ReactComponent as RemoveIcon} from "../../../statics/icons/icon_remove.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Form from "../../../common/components/shared/form/form";
import {Utils} from "../../../helpers/utils";


interface ActionFormState {
    selectedElement: number | undefined;
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: "removePopup" | "cancelPopup" | null;
    removeItemResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    cancelItemResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isCancelPopupActionWaiting: boolean;
}

interface ActionFormProps extends RouteComponentProps {
    isUpdate: boolean;
    actionId?: string;
}

class ActionForm extends React.Component <ActionFormProps, ActionFormState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            selectedElement: undefined,
            isSubmitButtonBlocked: false,
            response: undefined,
            displayPopup: null,
            removeItemResponse: undefined,
            cancelItemResponse: undefined,
            isCancelPopupActionWaiting: false
        };
    }

    componentWillUnmount() {
        this.context.timetableStore.clearStore();
    }

    render() {
        return (
            <Form
                class="action-form"
                renderError={this.renderErrors()}
                renderContent={this.renderContent()}
                isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                backAddress={{pathname: routingAddresses.timetable.week, state: "cancel"}}
                submit={this.submit.bind(this)}
            />
        );
    }

    renderErrors() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.props.isUpdate ? i18n.t("timetable.errors.editErrorSuffix") : i18n.t("timetable.errors.addErrorSuffix")}
                    translatedFields={{
                        ferryName: i18n.t("timetable.headers.ferryName"),
                        actions: i18n.t("timetable.headers.actionName"),
                    }}
                />
            );
        }
    }

    renderContent() {
        const timetableStore = this.context.timetableStore as ITimetableStore;

        return (
            <>
                {timetableStore.actions.map((element, actionIndex) =>
                    <FormSection
                        key={`form-section-${actionIndex}`}
                        actionId={this.props.actionId}
                        actionIndex={actionIndex}
                        isUpdate={this.props.isUpdate}
                        isVisibleRemoveButton={timetableStore.actions.length > 1 || this.props.isUpdate}
                        onRemoveAction={this.onRemoveAction.bind(this, actionIndex)}
                        isVisibleCancelButton={this.props.isUpdate}
                        onCancelAction={this.onCancelAction.bind(this, actionIndex)}
                    />
                )}
                {this.renderAddActionContent()}
            </>
        );
    }

    private onRemoveAction(actionId: number) {
        if (this.props.isUpdate) {
            this.setState({displayPopup: "removePopup"});
        } else {
            this.setState({selectedElement: actionId, displayPopup: "removePopup"});
        }
    }

    private onCancelAction(actionId: number) {
        this.setState({selectedElement: actionId, displayPopup: "cancelPopup"});
    }

    private addNextAction() {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        timetableStore.createAction();
    }

    protected renderAddActionContent() {
        return <>
            {this.renderRemovePopup(this.confirmRemove.bind(this))}
            {this.renderCancelPopup(this.confirmCancel.bind(this))}
            {!this.props.isUpdate && <Button
                text={i18n.t("timetable.actions.addNextAction")}
                type="primary"
                variant="text"
                className="add-next-action-btn"
                testId="add-next-action"
                onClick={() => this.addNextAction()}
            />}
        </>;
    }

    protected renderRemovePopup(confirmRemove: any) {
        const value = this.props.isUpdate ? i18n.t("timetable.actions.removeAction.action") : i18n.t("timetable.actions.removeAction.position");
        const header = `${i18n.t("timetable.actions.removeAction.header", {value: value})}`;
        const secondHeader = `${i18n.t("timetable.actions.removeAction.secondHeader", {value: value})}`;
        const content = <div>
            <p>{i18n.t("timetable.actions.removeAction.line1", {value: value})}</p>
            <p>{i18n.t("timetable.actions.removeAction.line2")}</p>
        </div>;

        return (
            <Popup
                open={this.state.displayPopup === "removePopup"}
                className="remove-popup popup-with-secondary-button-color"
                header={header}
                secondHeader={secondHeader}
                icon={<RemoveIcon/>}
                content={content}
                actionText={i18n.t("timetable.actions.removeAction.actionText")}
                action={confirmRemove}
                cancel={() => this.setState({displayPopup: null, removeItemResponse: undefined})}
                testId="remove-popup"
                httpError={
                    <>
                        {!Api.isSuccess(this.state.removeItemResponse) && (
                            <HttpError
                                response={this.state.removeItemResponse}
                                cause={i18n.t("timetable.actions.removeAction.errorMessagePart1")}
                            />
                        )}
                    </>
                }
            />
        );
    }

    private async confirmRemove() {
        if (this.props.isUpdate) {
            this.setState({removeItemResponse: undefined});

            const response = await TimetableService.removeAction(this.props.actionId!);

            this.setState({removeItemResponse: response});

            if (Api.isSuccess(response)) {
                this.props.history.push(routingAddresses.timetable.week, "remove");
                this.setState({selectedElement: undefined, displayPopup: null});
            }
        } else {
            const timetableStore = this.context.timetableStore as ITimetableStore;

            if (this.state.selectedElement !== undefined) {
                timetableStore.removeAction(this.state.selectedElement);

                this.setState({selectedElement: undefined, displayPopup: null});
            }
        }
    }

    protected renderCancelPopup(confirmCancel: any) {
        const content = <div>
            <p>{i18n.t("timetable.actions.cancelAction.line1")}</p>
            <p>{i18n.t("timetable.actions.cancelAction.line2")}</p>
        </div>;

        return (
            <Popup
                open={this.state.displayPopup === "cancelPopup"}
                className="cancel-popup popup-with-secondary-button-color"
                header={i18n.t("timetable.actions.cancelAction.header")}
                secondHeader={i18n.t("timetable.actions.cancelAction.secondHeader")}
                icon={<HighlightOffIcon />}
                content={content}
                actionText={i18n.t("timetable.actions.cancelAction.actionText")}
                action={confirmCancel}
                cancel={() => this.setState({displayPopup: null, cancelItemResponse: undefined})}
                testId="cancel-popup"
                httpError={
                    <>
                        {!Api.isSuccess(this.state.cancelItemResponse) && (
                            <HttpError
                                response={this.state.cancelItemResponse}
                                cause={i18n.t("timetable.actions.cancelAction.errorMessage")}
                            />
                        )}
                    </>
                }
                isWaiting={this.state.isCancelPopupActionWaiting}
            />
        );
    }

    private async confirmCancel() {
        this.setState({cancelItemResponse: undefined, isCancelPopupActionWaiting: true});

        const response = await TimetableService.cancelAction(this.props.actionId!);

        this.setState({cancelItemResponse: response});

        if (Api.isSuccess(response)) {
            this.props.history.push(routingAddresses.timetable.week, "cancel");
            this.setState({selectedElement: undefined, displayPopup: null});
        }

        this.setState({isCancelPopupActionWaiting: false});
    }

    public get isValid() {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        const actions = timetableStore.actions;
        let isValid = true;

        actions.map((action) => {
            if (!this.actionIsValid(action)) {
                isValid = false;
            }
        });

        return isValid;
    }

    public actionIsValid(action: IActionInStore) {
        action.ferryId.validate();
        action.actionType.validate();
        action.wharfId.validate();
        action.arrivalDate.validate();
        action.arrivalHour.validate();
        action.departureDate.validate();
        action.departureHour.validate();

        if (action.actionType.value === TimetableActionTypes.DEPARTURE && !this.routeAndLoadingSectionIsValid(action)) {
            return false;
        }

        return action.ferryId.isValid && action.actionType.isValid
            && action.wharfId.isValid && action.arrivalDate.isValid
            && action.arrivalHour.isValid && action.departureDate.isValid
            && action.departureHour.isValid;
    }

    public routeAndLoadingSectionIsValid(action: IActionInStore) {
        action.routes.validate();
        action.openParkingDate.validate();
        action.openParkingHour.validate();
        action.startLoadingDate.validate();
        action.startLoadingHour.validate();
        action.endLoadingDate.validate();
        action.endLoadingHour.validate();
        action.destinationPortId.validate();

        return action.routes.isValid && action.openParkingDate.isValid
            && action.openParkingHour.isValid && action.startLoadingDate.isValid
            && action.startLoadingHour.isValid && action.endLoadingDate.isValid
            && action.endLoadingHour.isValid && action.destinationPortId.isValid;
    }

    private async submit() {
        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyTimetable();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.timetable.week, "modify");
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyTimetable() {
        const timetableStore = this.context.timetableStore as ITimetableStore;
        this.setState({response: undefined});

        if (this.props.isUpdate && this.props.actionId) {
            const timetableAction = this.prepareTimetableAction(timetableStore.actions[0]);

            if (timetableAction) {
                this.setState({
                    response: await TimetableService.modifyAction({action: timetableAction}, this.props.actionId)
                });
            }
        } else {
            const timetableActions: NewTimetableActionModel[] = [];

            timetableStore.actions.forEach((action: IActionInStore) => {
                const timetableAction = this.prepareTimetableAction(action);

                if (timetableAction) {
                    timetableActions.push(timetableAction);
                }
            });

            this.setState({
                response: await TimetableService.addAction({actions: timetableActions})
            });

            if (Api.isSuccess(this.state.response)) {
                if (timetableStore.actions.length > 1) {
                    timetableStore.setLastAddElement(this.sortFromNewestToOldestDates(timetableActions));
                } else {
                    timetableStore.setLastAddElement(timetableActions[0]);
                }
            }
        }
    }

    // Sort the dates so that the most recent by time is at the top of the
    private sortFromNewestToOldestDates(actions: NewTimetableActionModel[]) {
        return actions.sort((a,b) => new Date(b.arrivalTime!.toString()).getTime() - new Date(a.arrivalTime!.toString()).getTime())[0];
    }

    private prepareTimetableAction(action: IActionInStore): NewTimetableActionModel | null {
        switch (action.actionType.value) {
            case TimetableActionTypes.ARRIVAL:
                return {
                    ferryId: action.ferryId.value,
                    actionType: action.actionType.value,
                    voyageNo: action.voyageNo.value.trim(),
                    wharfId: action.wharfId.value,
                    notes: action.notes.value.trim(),
                    arrivalTime: DateUtils.joinDateAndTimeToUtc(action.arrivalDate.value, action.arrivalHour.value),
                    departureTime: DateUtils.joinDateAndTimeToUtc(action.departureDate.value, action.departureHour.value),
                    actualArrivalTime: DateUtils.joinDateAndTimeToUtc(action.actualArrivalDate.value, action.actualArrivalHour.value),
                    actualDepartureTime: DateUtils.joinDateAndTimeToUtc(action.actualDepartureDate.value, action.actualDepartureHour.value),
                };
            case TimetableActionTypes.STOP:
                return {
                    ferryId: action.ferryId.value,
                    actionType: action.actionType.value,
                    wharfId: action.wharfId.value,
                    notes: action.notes.value.trim(),
                    arrivalTime: DateUtils.joinDateAndTimeToUtc(action.arrivalDate.value, action.arrivalHour.value),
                    departureTime: DateUtils.joinDateAndTimeToUtc(action.departureDate.value, action.departureHour.value),
                };
            case TimetableActionTypes.DEPARTURE:
                return {
                    ferryId: action.ferryId.value,
                    actionType: action.actionType.value,
                    voyageNo: action.voyageNo.value.trim(),
                    wharfId: action.wharfId.value,
                    notes: action.notes.value.trim(),
                    arrivalTime: DateUtils.joinDateAndTimeToUtc(action.arrivalDate.value, action.arrivalHour.value),
                    departureTime: DateUtils.joinDateAndTimeToUtc(action.departureDate.value, action.departureHour.value),
                    actualArrivalTime: DateUtils.joinDateAndTimeToUtc(action.actualArrivalDate.value, action.actualArrivalHour.value),
                    actualDepartureTime: DateUtils.joinDateAndTimeToUtc(action.actualDepartureDate.value, action.actualDepartureHour.value),
                    routes: action.routes.value,
                    openParkingTime: DateUtils.joinDateAndTimeToUtc(action.openParkingDate.value, action.openParkingHour.value),
                    startLoadingTime: DateUtils.joinDateAndTimeToUtc(action.startLoadingDate.value, action.startLoadingHour.value),
                    endLoadingTime: DateUtils.joinDateAndTimeToUtc(action.endLoadingDate.value, action.endLoadingHour.value),
                    destinationPortId: action.destinationPortId.value
                };
            default:
                return null;
        }
    }
}

export default withRouter(observer(ActionForm));
